import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import DataGrid from '../../Component/DataGrid/DataGrid';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import { SlideUpTransition } from '../../Component/Reusables/Transitions';
import PromoCodeServices from '../../Services/PromoCodeService';
import moment from 'moment';
import DownloadCsv from '../../Component/DataGrid/Plugins/DownloadCsv';

interface PromoCodeDetail {
    name: string;
    email: string;
    mobileNumber: string;
    promoCodeUserMappingId: string;
    userGuid: string;
    promoCodeId: string;
    couponCode: string;
    couponAmount: string;
    transactionPaymentId: string;
    couponTransactionExternalId: string;
    createdUtc: string;
    modifiedUtc: string;
    TotalRecords: number;
    is_used: number;
}

interface PromoCodeListProps {
    open: boolean;
    onClose?: (refresh: boolean, action: string) => void;
    accessToken: string;
    refreshData: () => void;
    selectedPromoData: any;
}

interface PromoCodeListState {
    accessToken: string;
    promoCodeDetails: PromoCodeDetail[];
    currentPage: number;
    currentRowsPerPage: number;
    totalRows: number;
    searchText: string;
    selectedFilter: string;
    filterValue: string;
    isLoading: boolean;
}

class PromoCodeList extends React.Component<PromoCodeListProps, PromoCodeListState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private tableColumns: TableColumn<PromoCodeDetail>[] = [
        {
            name: 'Name',
            selector: row => row.name || '-',
            sortable: true,
            grow: 2,
            wrap: true,
            id: 'name'
        },
        {
            name: 'Email',
            selector: row => row.email || '-',
            sortable: true,
            grow: 3,
            wrap: true,
            id: 'email'
        },
        {
            name: 'Mobile Number',
            selector: row => row.mobileNumber || '-',
            sortable: true,
            grow: 2,
            id: 'mobileNumber'
        },
        {
            name: 'Coupon Code',
            selector: row => row.couponCode || '-',
            sortable: true,
            grow: 2,
            id: 'couponCode'
        },
        {
            name: 'Coupon Amount',
            selector: row => `$ ${row.couponAmount}` || '-',
            sortable: true,
            right: true,
            grow: 2,
            id: 'couponAmount'
        },
        {
            name: 'Transaction ID',
            selector: row => row.couponTransactionExternalId || '-',
            sortable: true,
            grow: 4,
            wrap: true,
            id: 'couponTransactionExternalId'
        },
        {
            name: 'Created Date',
            selector: row => row.createdUtc ? moment(row.createdUtc).format('DD MMM YYYY') : '-',
            sortable: true,
            grow: 2,
            id: 'createdUtc'
        },
        {
            name: 'Modified Date',
            selector: row => row.modifiedUtc ? moment(row.modifiedUtc).format('DD MMM YYYY') : '-',
            sortable: true,
            grow: 2,
            id: 'modifiedUtc'
        },
    ];


    constructor(props: PromoCodeListProps) {
        super(props);
        this.state = {
            accessToken: '',
            promoCodeDetails: [],
            currentPage: 1,
            currentRowsPerPage: 10,
            totalRows: 0,
            searchText: '',
            selectedFilter: '',
            filterValue: '',
            isLoading: false,
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            if (!accessToken_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            this.setState({ accessToken: accessToken_.toString() }, () => {
                this.getPromoCodeDetails();
            });
        } catch (error) {
            console.error('Error loading access token:', error);
        }
    }

    private getPromoCodeDetails = async () => {
        const { accessToken, currentPage, currentRowsPerPage, searchText, selectedFilter, filterValue } = this.state;

        const pageIndex = currentPage;

        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: currentRowsPerPage
        };

        if (searchText) {
            pagination["searchText"] = searchText;
        }

        if (selectedFilter) {
            pagination[selectedFilter] = filterValue;
        }

        this.setState({ isLoading: true });

        const promo_code_id = this.props.selectedPromoData?.promo_code_id ?? "";

        try {
            const response = await PromoCodeServices.getPromocodeDetails(accessToken, pagination, promo_code_id);
            if (response?.ok) {
                const result = await response.json();
                this.setState({
                    promoCodeDetails: result.promoCodeDetails || [],
                    totalRows: result.totalPages
                });
            }
        } catch (error) {
            console.error("Error fetching promo code details:", error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handlePageChange = (page: number) => {
        this.setState({ currentPage: page }, this.getPromoCodeDetails);
    };

    handleRowsPerPageChange = (rows: number, page: number) => {
        this.setState({
            currentRowsPerPage: rows,
            currentPage: page
        }, this.getPromoCodeDetails);
    };

    handleSearch = (text: string) => {
        this.setState({
            searchText: text,
            currentPage: 1
        }, this.getPromoCodeDetails);
    };

    handleFilterChange = (selectedFilter: string, filterValue: string) => {
        this.setState({
            selectedFilter,
            filterValue,
            currentPage: 1
        }, this.getPromoCodeDetails);
    };

    render() {
        const { open, onClose } = this.props;
        const { promoCodeDetails, isLoading, totalRows, currentPage, currentRowsPerPage } = this.state;

        return (
            <Dialog
                open={open}
                onClose={() => onClose?.(false, "cancel")}
                fullWidth
                keepMounted
                maxWidth="xl"
                TransitionComponent={SlideUpTransition}
            >
                {/* <DialogTitle>Promo Code Details</DialogTitle> */}
                <DialogContent className="subMenuModal" style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
                    <DataGrid
                        title="Promo Code Users"
                        search="Search Users"
                        defaultSearch={this.state.searchText}
                        onSearch={this.handleSearch}
                        onFilterChange={this.handleFilterChange}
                        columns={this.tableColumns}
                        data={promoCodeDetails}
                        onChangePage={this.handlePageChange}
                        onChangeRowsPerPage={this.handleRowsPerPageChange}
                        pagination
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={currentPage}
                        exportCsv
                        prepareCSVCustom={this.prepareCSVCustom}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose?.(false, "cancel")}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    private prepareCSVCustom(objDownloadCSV: DownloadCsv<PromoCodeDetail>) {
        // Default to first item if available
        const firstItem = objDownloadCSV.props.data?.[0];
        const rawCouponCode = typeof firstItem?.couponCode === 'string' ? firstItem.couponCode : 'UnknownCode';
        const safeCouponCode = rawCouponCode.replace(/[^a-zA-Z0-9_-]/g, '') || 'UnknownCode';
        const fileName = `Promo_${safeCouponCode}_user_list`;
        objDownloadCSV.prepareCSV(fileName);
    }
}

export default PromoCodeList;